import React, { useEffect, useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Typography,
  Popconfirm,
  message,
  Row,
  Col,
  Statistic,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productApi, botApi, groupApi } from '../services/api';
import { formatBeijingTime } from '../utils/dateUtils';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Product {
  id: number;
  bot_id: number;
  bot_username?: string;
  bot_name?: string;
  merchant_user_id?: number;
  merchant_username?: string;
  merchant_name?: string;
  product_type: string;
  name: string;
  description: string;
  price: number;
  duration_days?: number;
  card_validity_days?: number;
  price_month?: number;
  price_year?: number;
  price_forever?: number;
  usage_method?: string;
  group_ids: number[];
  commission_rate?: number;
  allow_referral?: boolean;
  is_active: boolean;
  is_featured: boolean;
  image_url?: string;
  cover_type?: 'photo' | 'video';
  stock?: number;
  max_purchases?: number;
  sort_order?: number;
  total_sales: number;
  total_revenue: number;
  created_at: string;
}

interface ProductListResponse {
  items: Product[];
  total: number;
  page: number;
  pageSize: number;
}

interface ProductBatchDeleteResponse {
  deleted_count: number;
  failed_count: number;
  failed_items: Array<{ id: number; reason?: string }>;
}

interface GroupOption {
  id: number;
  name: string;
  bot_id?: number;
  telegram_group_id?: number;
}

const PRODUCT_FETCH_BATCH_SIZE = 200;

const fetchAllProducts = async ({
  search,
  bot_id,
}: {
  search?: string;
  bot_id?: number;
}): Promise<ProductListResponse> => {
  const firstPage = await productApi.getProducts({
    page: 1,
    pageSize: PRODUCT_FETCH_BATCH_SIZE,
    search,
    bot_id,
  }) as unknown as ProductListResponse;

  const firstItems = firstPage?.items || [];
  const total = firstPage?.total || firstItems.length;

  if (total <= firstItems.length) {
    return {
      ...firstPage,
      items: firstItems,
      total,
    };
  }

  const totalPages = Math.ceil(total / PRODUCT_FETCH_BATCH_SIZE);
  const remainingPages = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      productApi.getProducts({
        page: index + 2,
        pageSize: PRODUCT_FETCH_BATCH_SIZE,
        search,
        bot_id,
      }) as unknown as Promise<ProductListResponse>
    )
  );

  return {
    ...firstPage,
    items: [
      ...firstItems,
      ...remainingPages.flatMap((pageData) => pageData?.items || []),
    ],
    total,
    page: 1,
    pageSize: total,
  };
};

const formatDurationLabel = (durationDays?: number, priceType?: string) => {
  if (priceType === 'month') {
    return '1个月';
  }
  if (priceType === 'year') {
    return '1年';
  }
  if (priceType === 'forever') {
    return '永久';
  }
  if (!durationDays || durationDays <= 0) {
    return '-';
  }
  if (durationDays >= 36500) {
    return '永久';
  }
  if (durationDays === 365) {
    return '1年';
  }
  if (durationDays === 180) {
    return '半年';
  }
  if (durationDays === 30) {
    return '1个月';
  }
  return `${durationDays}天`;
};

const formatPriceLabel = (price: number) => `¥${price.toFixed(2)}`;

const formatMerchantLabel = (product: Product) => {
  if (product.merchant_username) {
    return `@${product.merchant_username}`;
  }
  if (product.merchant_name) {
    return product.merchant_name;
  }
  if (product.merchant_user_id) {
    return `用户#${product.merchant_user_id}`;
  }
  return '-';
};

const getDurationTiers = (product: Product) => {
  if (product.product_type !== 'group') {
    return [];
  }
  const tiers: Array<{ type: string; days: number; price: number }> = [];
  if (product.price_month && product.price_month > 0) {
    tiers.push({ type: 'month', days: 30, price: product.price_month });
  }
  if (product.price_year && product.price_year > 0) {
    tiers.push({ type: 'year', days: 365, price: product.price_year });
  }
  if (product.price_forever && product.price_forever > 0) {
    tiers.push({ type: 'forever', days: 36500, price: product.price_forever });
  }
  return tiers.sort((a, b) => a.days - b.days);
};

const getMinDurationInfo = (product: Product) => {
  if (product.product_type === 'card') {
    if (product.card_validity_days && product.card_validity_days > 0) {
      return {
        days: product.card_validity_days,
        label: formatDurationLabel(product.card_validity_days),
      };
    }
    return {
      days: 36500,
      label: '永久',
    };
  }
  const tiers = getDurationTiers(product);
  if (tiers.length > 0) {
    return {
      days: tiers[0].days,
      label: tiers.map((tier) => formatDurationLabel(tier.days, tier.type)).join('/'),
    };
  }
  if (product.duration_days && product.duration_days > 0) {
    return {
      days: product.duration_days,
      label: formatDurationLabel(product.duration_days),
    };
  }
  return { days: 0, label: '-' };
};

const getDisplayPrice = (product: Product) => {
  const tiers = getDurationTiers(product);
  if (tiers.length > 0) {
    return tiers.map((tier) => formatPriceLabel(tier.price)).join('/');
  }
  return formatPriceLabel(product.price);
};

const getPriceSortValue = (product: Product) => {
  const tiers = getDurationTiers(product);
  if (tiers.length > 0) {
    return tiers[0].price;
  }
  return product.price || 0;
};

const ProductManagement: React.FC = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [searchText, setSearchText] = useState('');
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>(undefined);
  const [selectedProductIds, setSelectedProductIds] = useState<number[]>([]);
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'ascend' | 'descend' | undefined>(undefined);
  const [form] = Form.useForm();
  const modalBotId = Form.useWatch('bot_id', form);
  const modalProductType = Form.useWatch('product_type', form);
  const queryClient = useQueryClient();

  // 获取机器人列表
  const { data: botsData } = useQuery(
    ['bots-list'],
    () => botApi.getBots({ page: 1, pageSize: 100 }),
    {
      staleTime: 60000, // 1分钟内不重新请求
    }
  );
  const botsList = (botsData as any)?.items || [];

  const formBotId = modalBotId || editingProduct?.bot_id || selectedBotId;
  const { data: groupsData, isLoading: isGroupsLoading } = useQuery(
    ['groups-for-product-form', formBotId],
    () =>
      groupApi.getGroups({
        page: 1,
        pageSize: 100,
        bot_id: formBotId,
      }),
    {
      enabled: isModalVisible && Boolean(formBotId),
      staleTime: 60000,
    }
  );
  const groupsList = (groupsData as any)?.items || [];

  // 获取商品列表
  const { data: productsData, isLoading } = useQuery(
    ['products', searchText, selectedBotId],
    () =>
      fetchAllProducts({
        search: searchText,
        bot_id: selectedBotId,
      }),
    {
      keepPreviousData: true,
    }
  );

  // 创建商品
  const createMutation = useMutation(productApi.createProduct, {
    onSuccess: () => {
      message.success('商品创建成功');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: () => {
      message.error('商品创建失败');
    },
  });

  // 更新商品
  const updateMutation = useMutation(
    ({ id, data }: { id: number; data: any }) => productApi.updateProduct(id, data),
    {
      onSuccess: () => {
        message.success('商品更新成功');
        setIsModalVisible(false);
        form.resetFields();
        setEditingProduct(null);
        queryClient.invalidateQueries({ queryKey: ['products'] });
      },
      onError: () => {
        message.error('商品更新失败');
      },
    }
  );

  // 删除商品
  const deleteMutation = useMutation(productApi.deleteProduct, {
    onSuccess: () => {
      message.success('商品删除成功');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: () => {
      message.error('商品删除失败');
    },
  });

  // 批量删除商品
  const batchDeleteMutation = useMutation(productApi.batchDeleteProducts, {
    onError: () => {
      message.error('批量删除失败');
    },
  });

  // 切换商品状态
  const toggleStatusMutation = useMutation(productApi.toggleProductStatus, {
    onSuccess: () => {
      message.success('状态更新成功');
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: () => {
      message.error('状态更新失败');
    },
  });

  // 从 API 获取真实数据
  const products = (productsData as any)?.items || [];
  const totalProducts = (productsData as any)?.total || 0;
  const hasActiveFilters = Boolean(searchText.trim()) || selectedBotId !== undefined;
  const isDeleting = deleteMutation.isLoading || batchDeleteMutation.isLoading;

  useEffect(() => {
    setSelectedProductIds((current) =>
      current.filter((id) => products.some((product: Product) => product.id === id))
    );
  }, [products]);

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({
      bot_id: selectedBotId || botsList[0]?.id,
      product_type: 'group',
      price: 0,
      duration_days: 30,
      group_ids: [],
      commission_rate: 0,
      allow_referral: true,
      is_active: false,
      is_featured: false,
      cover_type: 'photo',
      sort_order: 0,
    });
    setIsModalVisible(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    form.resetFields();
    form.setFieldsValue({
      ...product,
      product_type: product.product_type || 'group',
      price: product.price ?? 0,
      duration_days: product.product_type === 'card' ? undefined : product.duration_days ?? 30,
      card_validity_days: product.product_type === 'card' ? product.card_validity_days : undefined,
      group_ids: product.group_ids || [],
      commission_rate: product.commission_rate ?? 0,
      allow_referral: product.allow_referral ?? true,
      is_active: Boolean(product.is_active),
      is_featured: Boolean(product.is_featured),
      cover_type: product.cover_type || 'photo',
      sort_order: product.sort_order ?? 0,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setSelectedProductIds((current) => current.filter((item) => item !== id));
      },
    });
  };

  const handleToggleStatus = (id: number) => {
    toggleStatusMutation.mutate(id);
  };

  const executeBatchDelete = async (ids: number[], actionLabel: string) => {
    const result = await batchDeleteMutation.mutateAsync(ids) as unknown as ProductBatchDeleteResponse;

    setSelectedProductIds((current) => current.filter((id) => !ids.includes(id)));
    await queryClient.invalidateQueries({ queryKey: ['products'] });

    const deletedCount = result?.deleted_count || 0;
    const failedCount = result?.failed_count || 0;
    const failedItems = result?.failed_items || [];

    if (failedCount > 0) {
      const failedSummary = failedItems
        .slice(0, 3)
        .map((item: { id: number; reason?: string }) => `#${item.id}${item.reason ? `（${item.reason}）` : ''}`)
        .join('，');

      if (deletedCount > 0) {
        message.warning(
          `${actionLabel}完成：已删除 ${deletedCount} 个，失败 ${failedCount} 个${failedSummary ? `（${failedSummary}）` : ''}`
        );
        return;
      }

      message.error(failedSummary || `${actionLabel}失败`);
      return;
    }

    message.success(`${actionLabel}成功，已删除 ${deletedCount} 个商品`);
  };

  const showBatchDeleteConfirm = ({
    ids,
    title,
    content,
    actionLabel,
  }: {
    ids: number[];
    title: string;
    content: React.ReactNode;
    actionLabel: string;
  }) => {
    Modal.confirm({
      title,
      content,
      okText: '确认删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await executeBatchDelete(ids, actionLabel);
        } catch {
          return;
        }
      },
    });
  };

  const handleBatchDeleteSelected = () => {
    if (!selectedProductIds.length) {
      message.warning('请先选择要删除的商品');
      return;
    }

    showBatchDeleteConfirm({
      ids: selectedProductIds,
      title: '确定批量删除选中的商品吗？',
      content: (
        <div>
          <div>本次将删除 {selectedProductIds.length} 个已选商品。</div>
          <div style={{ marginTop: 8 }}>
            删除后会同步清理关联订单、支付和卡密，并保留备份文件。
          </div>
        </div>
      ),
      actionLabel: '批量删除',
    });
  };

  const handleDeleteCurrentResults = () => {
    if (!products.length) {
      message.warning('当前没有可删除的商品');
      return;
    }

    const ids = products.map((product: Product) => product.id);
    const title = hasActiveFilters ? '确定删除当前筛选结果吗？' : '确定一键删除全部商品吗？';
    const actionLabel = hasActiveFilters ? '删除当前筛选结果' : '一键全部删除';

    showBatchDeleteConfirm({
      ids,
      title,
      content: (
        <div>
          <div>
            {hasActiveFilters
              ? `当前筛选结果共 ${ids.length} 个商品，确认后将全部删除。`
              : `当前未设置筛选条件，确认后将删除全部 ${ids.length} 个商品。`}
          </div>
          <div style={{ marginTop: 8 }}>
            删除后会同步清理关联订单、支付和卡密，并保留备份文件。
          </div>
        </div>
      ),
      actionLabel,
    });
  };

  const handleModalOk = () => {
    form.validateFields().then((values) => {
      const payload = {
        bot_id: values.bot_id,
        name: values.name,
        description: values.description || '',
        product_type: values.product_type || 'group',
        price: values.price ?? 0,
        duration_days: values.product_type === 'group' ? values.duration_days : undefined,
        card_validity_days: values.product_type === 'card' ? values.card_validity_days : undefined,
        usage_method: values.usage_method || undefined,
        price_month: values.product_type === 'group' ? values.price_month : undefined,
        price_year: values.product_type === 'group' ? values.price_year : undefined,
        price_forever: values.product_type === 'group' ? values.price_forever : undefined,
        group_ids: values.product_type === 'group' ? (values.group_ids || []) : [],
        commission_rate: values.commission_rate ?? 0,
        allow_referral: values.allow_referral ?? true,
        stock: values.stock,
        max_purchases: values.max_purchases,
        is_active: Boolean(values.is_active),
        is_featured: Boolean(values.is_featured),
        image_url: values.image_url || undefined,
        cover_type: values.cover_type || 'photo',
        sort_order: values.sort_order ?? 0,
      };

      if (editingProduct) {
        const { bot_id: _botId, ...updatePayload } = payload;
        updateMutation.mutate({
          id: editingProduct.id,
          data: updatePayload,
        });
      } else {
        createMutation.mutate(payload);
      }
    });
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingProduct(null);
  };

  const getSortOrder = (field: string) => sortField === field ? sortOrder : undefined;

  const rowSelection = {
    selectedRowKeys: selectedProductIds,
    onChange: (keys: React.Key[]) => {
      setSelectedProductIds(
        keys
          .map((key) => Number(key))
          .filter((id) => Number.isFinite(id) && id > 0)
      );
    },
    preserveSelectedRowKeys: true,
    getCheckboxProps: () => ({
      disabled: isDeleting,
    }),
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a: Product, b: Product) => a.id - b.id,
      sortOrder: getSortOrder('id'),
    },
    {
      title: '所属机器人',
      key: 'bot',
      width: 180,
      render: (_: any, record: Product) => (
        <div>
          <div className="font-medium text-blue-600">
            {record.bot_username ? `@${record.bot_username}` : '-'}
          </div>
          <div className="text-xs text-gray-500">{record.bot_name || '-'}</div>
        </div>
      ),
      sorter: (a: Product, b: Product) => {
        const nameA = a.bot_name || a.bot_username || '';
        const nameB = b.bot_name || b.bot_username || '';
        return nameA.localeCompare(nameB);
      },
      sortOrder: getSortOrder('bot'),
    },
    {
      title: '所属商户',
      key: 'merchant',
      width: 170,
      render: (_: any, record: Product) => (
        <div>
          <div className="font-medium">{formatMerchantLabel(record)}</div>
          <div className="text-xs text-gray-500">
            {record.merchant_user_id ? `ID: ${record.merchant_user_id}` : '-'}
          </div>
        </div>
      ),
      sorter: (a: Product, b: Product) => {
        const merchantA = a.merchant_username || a.merchant_name || `${a.merchant_user_id || ''}`;
        const merchantB = b.merchant_username || b.merchant_name || `${b.merchant_user_id || ''}`;
        return merchantA.localeCompare(merchantB);
      },
      sortOrder: getSortOrder('merchant'),
    },
    {
      title: '商品名称',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => <span className="font-medium">{text}</span>,
      sorter: (a: Product, b: Product) => a.name.localeCompare(b.name),
      sortOrder: getSortOrder('name'),
    },
    {
      title: '商品类型',
      dataIndex: 'product_type',
      key: 'product_type',
      width: 100,
      render: (type: string) => (
        <Tag color={type === 'group' ? 'blue' : 'green'}>
          {type === 'group' ? '会员群' : '卡密'}
        </Tag>
      ),
      sorter: (a: Product, b: Product) => a.product_type.localeCompare(b.product_type),
      sortOrder: getSortOrder('product_type'),
    },
    {
      title: '价格',
      dataIndex: 'price',
      key: 'price',
      render: (_price: number, record: Product) => (
        <span className="text-green-600 font-semibold">{getDisplayPrice(record)}</span>
      ),
      sorter: (a: Product, b: Product) => getPriceSortValue(a) - getPriceSortValue(b),
      sortOrder: getSortOrder('price'),
    },
    {
      title: '时长',
      key: 'duration_days',
      render: (_: number, record: Product) => getMinDurationInfo(record).label,
      sorter: (a: Product, b: Product) => getMinDurationInfo(a).days - getMinDurationInfo(b).days,
      sortOrder: getSortOrder('duration_days'),
    },
    {
      title: '销量',
      dataIndex: 'total_sales',
      key: 'total_sales',
      render: (count: number) => <span className="font-medium">{count}</span>,
      sorter: (a: Product, b: Product) => a.total_sales - b.total_sales,
      sortOrder: getSortOrder('total_sales'),
    },
    {
      title: '库存',
      dataIndex: 'stock',
      key: 'stock',
      render: (stock: number) => stock !== null && stock !== undefined ? stock : '无限',
      sorter: (a: Product, b: Product) => {
        const stockA = a.stock !== null && a.stock !== undefined ? a.stock : Infinity;
        const stockB = b.stock !== null && b.stock !== undefined ? b.stock : Infinity;
        return stockA - stockB;
      },
      sortOrder: getSortOrder('stock'),
    },
    {
      title: '上架时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (date: string) => date ? formatBeijingTime(date) : '-',
      sorter: (a: Product, b: Product) => (a.created_at || '').localeCompare(b.created_at || ''),
      sortOrder: getSortOrder('created_at'),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? '上架' : '下架'}
        </Tag>
      ),
      sorter: (a: Product, b: Product) => Number(a.is_active) - Number(b.is_active),
      sortOrder: getSortOrder('is_active'),
    },
    {
      title: '操作',
      key: 'action',
      render: (record: Product) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            size="small"
            title="查看详情"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
            title="编辑"
          />
          <Switch
            size="small"
            checked={Boolean(record.is_active)}
            onChange={() => handleToggleStatus(record.id)}
            checkedChildren="上架"
            unCheckedChildren="下架"
            title="切换状态"
          />
          <Popconfirm
            title="确定删除这个商品吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              icon={<DeleteOutlined />}
              size="small"
              danger
              title="删除"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* 页面标题和统计 */}
      <div>
        <Title level={2} className="mb-4">
          商品管理
        </Title>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="总商品数"
                value={totalProducts}
                prefix={<ShoppingOutlined className="text-blue-500" />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="上架商品"
                value={products.filter((p: Product) => p.is_active).length}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card>
              <Statistic
                title="总销量"
                value={products.reduce((sum: number, p: Product) => sum + p.total_sales, 0)}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* 操作栏 */}
      <Card>
        <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
          <Space wrap>
            <Select
              placeholder="选择机器人"
              allowClear
              style={{ width: 200 }}
              value={selectedBotId}
              onChange={(value) => setSelectedBotId(value)}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
              }
            >
              {botsList.map((bot: any) => (
                <Option key={bot.id} value={bot.id}>
                  {bot.bot_name || bot.bot_username || `机器人 ${bot.id}`}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="排序方式"
              allowClear
              style={{ width: 150 }}
              value={sortField ? `${sortField}-${sortOrder}` : undefined}
              onChange={(value) => {
                if (value) {
                  const [field, order] = value.split('-');
                  setSortField(field);
                  setSortOrder(order as 'ascend' | 'descend');
                } else {
                  setSortField('');
                  setSortOrder(undefined);
                }
              }}
            >
              <Option value="price-ascend">价格升序</Option>
              <Option value="price-descend">价格降序</Option>
              <Option value="total_sales-ascend">销量升序</Option>
              <Option value="total_sales-descend">销量降序</Option>
              <Option value="created_at-ascend">上架时间升序</Option>
              <Option value="created_at-descend">上架时间降序</Option>
            </Select>
            <Input.Search
              placeholder="搜索商品名称、机器人或所属商户"
              allowClear
              style={{ width: 300 }}
              onSearch={setSearchText}
            />
          </Space>
          <Space wrap>
            {selectedProductIds.length > 0 && (
              <Text type="secondary">已选 {selectedProductIds.length} 项</Text>
            )}
            <Button
              danger
              disabled={!selectedProductIds.length || isDeleting}
              loading={batchDeleteMutation.isLoading}
              onClick={handleBatchDeleteSelected}
            >
              批量删除
            </Button>
            <Button
              danger
              disabled={!products.length || isDeleting}
              loading={batchDeleteMutation.isLoading}
              onClick={handleDeleteCurrentResults}
            >
              {hasActiveFilters ? `删除当前结果（${products.length}）` : `一键全部删除（${products.length}）`}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              添加商品
            </Button>
          </Space>
        </div>

        {/* 商品表格 */}
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          rowSelection={rowSelection}
          loading={isLoading}
          sortDirections={['ascend', 'descend', 'ascend']}
          onChange={(_pagination, _filters, sorter: any) => {
            if (sorter && !Array.isArray(sorter)) {
              const field = (sorter.columnKey || sorter.field) as string;
              setSortField(sorter.order ? field : '');
              setSortOrder(sorter.order as 'ascend' | 'descend' | undefined);
            }
          }}
          pagination={{
            total: totalProducts,
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 添加/编辑商品弹窗 */}
      <Modal
        title={editingProduct ? '编辑商品' : '添加商品'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={760}
        confirmLoading={createMutation.isLoading || updateMutation.isLoading}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            product_type: 'group',
            price: 0,
            duration_days: 30,
            group_ids: [],
            commission_rate: 0,
            allow_referral: true,
            is_active: false,
            is_featured: false,
            cover_type: 'photo',
            sort_order: 0,
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="bot_id"
                label="所属机器人"
                rules={[{ required: true, message: '请选择所属机器人' }]}
              >
                <Select
                  placeholder="请选择所属机器人"
                  disabled={Boolean(editingProduct)}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={() => form.setFieldValue('group_ids', [])}
                >
                  {botsList.map((bot: any) => (
                    <Option key={bot.id} value={bot.id}>
                      {bot.bot_name || bot.bot_username || `机器人 ${bot.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="product_type"
                label="商品类型"
                rules={[{ required: true, message: '请选择商品类型' }]}
              >
                <Select
                  placeholder="请选择商品类型"
                  onChange={(value) => {
                    if (value !== 'group') {
                      form.setFieldValue('group_ids', []);
                      form.setFieldValue('duration_days', undefined);
                      form.setFieldValue('card_validity_days', undefined);
                      form.setFieldValue('price_month', undefined);
                      form.setFieldValue('price_year', undefined);
                      form.setFieldValue('price_forever', undefined);
                    } else if (!form.getFieldValue('duration_days')) {
                      form.setFieldValue('duration_days', 30);
                    }
                  }}
                >
                  <Option value="group">会员群</Option>
                  <Option value="card">卡密</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="name"
            label="商品名称"
            rules={[{ required: true, message: '请输入商品名称' }]}
          >
            <Input placeholder="请输入商品名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="商品描述"
          >
            <TextArea rows={3} placeholder="请输入商品描述" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={modalProductType === 'group' ? 12 : 24}>
              <Form.Item
                name="price"
                label="价格 (¥)"
                rules={[{ required: true, message: '请输入价格' }]}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="请输入价格"
                />
              </Form.Item>
            </Col>
            {modalProductType === 'group' && (
              <Col span={12}>
                <Form.Item
                  name="duration_days"
                  label="有效期 (天)"
                >
                  <InputNumber
                    min={1}
                    style={{ width: '100%' }}
                    placeholder="请输入有效期"
                  />
                </Form.Item>
              </Col>
            )}
          </Row>

          {modalProductType === 'group' && (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item name="price_month" label="月价格 (¥)">
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="price_year" label="年价格 (¥)">
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item name="price_forever" label="永久价格 (¥)">
                    <InputNumber min={0} precision={2} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="group_ids"
                label="包含群组"
                rules={[{ required: true, message: '请选择包含的群组' }]}
              >
                <Select
                  mode="multiple"
                  placeholder={formBotId ? '请选择包含的群组' : '请先选择所属机器人'}
                  loading={isGroupsLoading}
                  disabled={!formBotId}
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  showSearch
                  filterOption={(input, option) =>
                    String(option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {groupsList.map((group: GroupOption) => (
                    <Option key={group.id} value={group.id}>
                      {group.name || `群组 ${group.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}

          {modalProductType === 'card' && (
            <>
              <Form.Item
                name="card_validity_days"
                label="卡密有效期 (天)"
                extra="留空表示永久有效；填写正整数则显示对应有效期。"
              >
                <InputNumber
                  min={1}
                  max={36500}
                  style={{ width: '100%' }}
                  placeholder="留空为永久"
                />
              </Form.Item>
              <Form.Item name="usage_method" label="使用方法">
                <TextArea rows={3} placeholder="请输入卡密使用方法" />
              </Form.Item>
            </>
          )}

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="stock" label="库存">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="留空为无限" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="max_purchases" label="每人限购">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="留空不限" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="sort_order" label="排序权重">
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="commission_rate" label="分销比例">
                <InputNumber
                  min={0}
                  max={1}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  placeholder="0-1"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="cover_type" label="封面类型">
                <Select>
                  <Option value="photo">图片</Option>
                  <Option value="video">视频</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="image_url" label="封面链接">
                <Input placeholder="请输入封面链接" />
              </Form.Item>
            </Col>
          </Row>

          <Space size="large" wrap>
            <Form.Item name="is_active" label="上架状态" valuePropName="checked">
              <Switch checkedChildren="上架" unCheckedChildren="下架" />
            </Form.Item>
            <Form.Item name="is_featured" label="推荐商品" valuePropName="checked">
              <Switch checkedChildren="推荐" unCheckedChildren="普通" />
            </Form.Item>
            <Form.Item name="allow_referral" label="允许分销" valuePropName="checked">
              <Switch checkedChildren="允许" unCheckedChildren="禁止" />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default ProductManagement;
